import {
  createContext,
  Dispatch,
  Fragment,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { IntlProvider } from 'react-intl';

import { I18N_LOG_MISSING_TRANSLATIONS } from '@/config/runtime-env';
import { Spinner } from '@/components/ui/spinner';
import { useAuthContext } from '@/contexts/auth-context';
import { queryKeys } from '@/query-keys';
import {
  getInitialBrowserLocale,
  resolveUserLocaleString,
} from '@/i18n/resolve-user-locale';

import { RTL_INTL_MESSAGE_LOCALES } from '../intl-message-locales';
import { DEFAULT_MESSAGES, loadedMessages, loadMessages } from '../messages';
import {
  AvailableLocale,
  defaultRichTextElements,
  timezone as defaultTimezone,
} from './shared';

export type LanguageContextType = {
  locale: AvailableLocale;
  setLocale: Dispatch<SetStateAction<AvailableLocale>>;
  timezone: string;
  setTimezone: Dispatch<SetStateAction<string>>;
};

export const LanguageContext = createContext<LanguageContextType>(
  // Default context
  {
    locale: getInitialBrowserLocale(),
    setLocale: () => void 0,
    timezone: defaultTimezone,
    setTimezone: () => void 0,
  },
);

export default function LanguageProvider({ children }: PropsWithChildren) {
  const { whoami, isLoading } = useAuthContext();
  const browserLocale = useMemo(() => getInitialBrowserLocale(), []);

  const [locale, setLocale] = useState<AvailableLocale>(browserLocale);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [hasShownContent, setHasShownContent] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (whoami?.locale) {
      setLocale(resolveUserLocaleString(whoami.locale, browserLocale));
    } else {
      setLocale(browserLocale);
    }

    if (whoami?.time_zone) {
      setTimezone(whoami.time_zone);
    } else {
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }, [browserLocale, isLoading, whoami]);

  // The page keeps the last catalog it showed until the next one loads.
  const { data: shown, isPending } = useQuery({
    initialData: () => {
      const messages = loadedMessages(locale);
      return messages && { locale, messages };
    },
    placeholderData: keepPreviousData,
    queryFn: async () => ({ locale, messages: await loadMessages(locale) }),
    queryKey: queryKeys.i18nCatalog(locale),
    staleTime: Infinity,
  });
  const shownLocale = shown?.locale ?? 'en-US';

  useEffect(() => {
    document.documentElement.dir = RTL_INTL_MESSAGE_LOCALES.has(shownLocale)
      ? 'rtl'
      : 'ltr';
  }, [shownLocale]);

  const handleError = (error: Error & { code: string }) => {
    if (
      I18N_LOG_MISSING_TRANSLATIONS === 'true' &&
      error.code === 'MISSING_TRANSLATION'
    ) {
      // Make missing translations errors less visible as they are very verbose and can hide more valuable error messages
      // eslint-disable-next-line no-console
      console.warn(error.message);
    }
  };

  // A rendered fallback is still a running page; later loads must not unmount it.
  if (!isPending && !hasShownContent) setHasShownContent(true);
  if (isPending && !hasShownContent) return <Spinner />;

  return (
    <LanguageContext.Provider
      value={{ locale, setLocale, timezone, setTimezone }}
    >
      <IntlProvider
        locale={shownLocale}
        key={shownLocale}
        messages={shown?.messages ?? DEFAULT_MESSAGES}
        textComponent={Fragment}
        defaultRichTextElements={defaultRichTextElements}
        onError={handleError}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
}
